"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "freezer-btu-calculator",
  "refrigerator-size-calculator",
  "freezer-energy-cost-calculator",
  "appliance-energy-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Outdoor Refrigerator BTU Calculator",
  category: "Calculator" as const,
  mediumDescription:
    "Spec the cooling capacity an outdoor refrigerator needs based on volume, climate, sun exposure, and cabinet insulation. Built on ASHRAE heat-load principles and outdoor-rated refrigerator manufacturer specifications.",
  faqs: [
    { question: "Why does an outdoor fridge need more BTUs than indoor?", answer: "Outdoor units fight ambient heat that indoor fridges never see — direct sun adds 25–50% to the heat load, and 90°F+ ambient adds another 40–80%. The compressor has to work harder against those loads." },
    { question: "What's the difference between a standard fridge and an outdoor-rated fridge?", answer: "Outdoor-rated units have weather-sealed gaskets, UV-resistant exteriors (usually stainless), enhanced ventilation, and compressors specced for ambient temperatures from roughly 32°F to 110°F." },
    { question: "How much BTU per cubic foot does an outdoor fridge need?", answer: "Indoor baseline is roughly 100 BTU per cu ft. Outdoor in moderate climate runs 140 BTU/cu ft; hot climate (90–110°F) needs 180; very hot (110°F+) needs 220." },
    { question: "Does putting an outdoor fridge in shade really matter?", answer: "Substantially. Full sun adds about 50% to the heat load vs full shade. Even partial shade (the partial-sun setting in this tool) cuts about 25% of the solar gain." },
    { question: "Should I install my outdoor fridge in an insulated cabinet?", answer: "Yes if possible. Insulated cabinets cut about 15% of the heat load by isolating the unit from ambient temperature swings — and protect the compressor from rain and debris." },
    { question: "What size electrical circuit does an outdoor fridge need?", answer: "Most outdoor refrigerators run on a standard 110V/15A circuit, but high-BTU units (1800+ BTU) often need a dedicated 20A circuit. Check the spec sheet — and have a licensed electrician do the install." },
  ],
  relatedTools: ["freezer-btu-calculator", "refrigerator-size-calculator", "freezer-energy-cost-calculator"],
}

export function OutdoorRefrigeratorBtuPageClient() {
  const [cubicFeet, setCubicFeet] = useState("5")
  const [climate, setClimate] = useState("moderate")
  const [sunExposure, setSunExposure] = useState("partial")
  const [insulated, setInsulated] = useState(false)

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    // Outdoor-rated cabinets run 1 cu ft (undercounter beverage cooler) to 50.
    const cuFt = readQuantity(cubicFeet, { min: 1, max: 50 })
    if (cuFt === null) return null

    const baseBTU = cuFt * 100

    let climateMultiplier = 1.0
    if (climate === "hot") climateMultiplier = 1.8
    else if (climate === "very-hot") climateMultiplier = 2.2
    else if (climate === "moderate") climateMultiplier = 1.4
    else climateMultiplier = 1.2 // mild

    let sunMultiplier = 1.0
    if (sunExposure === "full") sunMultiplier = 1.5
    else if (sunExposure === "partial") sunMultiplier = 1.25
    else sunMultiplier = 1.1 // shaded

    const insulationMultiplier = insulated ? 0.85 : 1.0
    const adjustedBTU = baseBTU * climateMultiplier * sunMultiplier * insulationMultiplier
    const recommendedBTU = Math.ceil(adjustedBTU * 1.2)

    let compressorSize = ""
    if (recommendedBTU < 500) compressorSize = "1/8 HP compressor"
    else if (recommendedBTU < 800) compressorSize = "1/6 HP compressor"
    else if (recommendedBTU < 1200) compressorSize = "1/5 HP compressor"
    else if (recommendedBTU < 1800) compressorSize = "1/4 HP compressor"
    else if (recommendedBTU < 2500) compressorSize = "1/3 HP compressor"
    else compressorSize = "1/2 HP+ compressor"

    return {
      baseBTU: Math.round(baseBTU),
      adjustedBTU: Math.round(adjustedBTU),
      recommendedBTU,
      compressorSize,
    }
  }, [cubicFeet, climate, sunExposure, insulated])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="What BTU rating for"
      heroTitleItalic="your"
      heroTitlePost="outdoor refrigerator?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~25 sec", "ASHRAE heat-load + outdoor specs"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "100", label: "BTU per cu ft", note: "Indoor base" },
            { value: "+50%", label: "Full sun load", note: "vs shaded" },
            { value: "+80%", label: "Hot ambient", note: "90–110°F" },
            { value: "20%", label: "Safety margin", note: "Recommended" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "outdoor-refrigerator-btu-calculator")}
      cta={{
        title: "Need an outdoor-rated unit, not just any fridge?",
        body: "Browse bottom-freezer refrigerators — many models are rated for high-ambient and outdoor-cabinet installation.",
        buttonLabel: "Browse bottom-freezer fridges",
        buttonHref: "/collections/bottom-freezer-refrigerators",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Volume.
                <br />
                Climate. Sun.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Outdoor fridges fight 40–120% more heat load than indoor units. We size with a 20% safety margin.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Refrigerator size
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={50}
                      step="0.1"
                      value={cubicFeet}
                      onChange={(e) => setCubicFeet(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">cu ft</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Climate (peak summer)
                  </label>
                  <select
                    value={climate}
                    onChange={(e) => setClimate(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="mild">Mild (50–70°F) — ×1.2</option>
                    <option value="moderate">Moderate (70–90°F) — ×1.4</option>
                    <option value="hot">Hot (90–110°F) — ×1.8</option>
                    <option value="very-hot">Very hot (110°F+) — ×2.2</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Sun exposure
                  </label>
                  <select
                    value={sunExposure}
                    onChange={(e) => setSunExposure(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="shaded">Fully shaded — ×1.1</option>
                    <option value="partial">Partial sun — ×1.25</option>
                    <option value="full">Full sun — ×1.5</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={insulated}
                      onChange={(e) => setInsulated(e.target.checked)}
                      className="w-5 h-5 accent-[#ff9951]"
                    />
                    <span className="text-[14px] font-bold text-gray-900">Installed in insulated outdoor cabinet (-15%)</span>
                  </label>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Insulated cabinets cut load and protect the unit from weather.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Recommended cooling capacity
                  </div>
                  <div className="font-black" style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}>
                    {result?.recommendedBTU ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">BTU/hr</span>
                  </div>
                  <div className="mt-4 text-[15px] text-white/85 leading-[1.5]">
                    {result ? `${result.compressorSize}. Includes 20% safety margin over baseline ${result.adjustedBTU} BTU.` : "Enter unit size and conditions to see required BTU."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Indoor baseline</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.baseBTU ?? "—"} BTU</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Compressor size</div>
                      <div className="text-[14px] sm:text-[16px] font-black tracking-tight">{result?.compressorSize ?? "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Match the spec"
          title="Bottom-freezer refrigerators rated for high ambient"
          description="Many bottom-freezer models are rated for outdoor cabinets and high-ambient installations — check spec sheets for operating range."
          collectionType="bottom-freezer"
          fallbackCollectionType="counter-depth"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                Heat load math, <em className="italic text-[#ff9951]">outdoor.</em>
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Built on ASHRAE refrigeration heat-load principles. Outdoor multipliers reflect manufacturer-published ambient and solar-gain adjustments for outdoor-rated refrigerators.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Volume baseline", d: "Indoor baseline is 100 BTU/hr per cu ft of refrigerated volume — the starting point for any heat-load estimate." },
                { n: "02", t: "Climate multiplier", d: "Mild ×1.2, moderate ×1.4, hot ×1.8, very hot ×2.2. Reflects the work the compressor does against ambient temperature." },
                { n: "03", t: "Sun multiplier", d: "Shaded ×1.1, partial ×1.25, full sun ×1.5. Solar gain on the cabinet adds heat that has to be pumped back out." },
                { n: "04", t: "Insulation + safety margin", d: "Insulated cabinet ×0.85 (saves 15%). We add a final 20% safety margin so the compressor isn't running flat out at peak load." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Counter-depth refrigerators for outdoor cabinets"
          description="Shallower cabinet depth fits standard outdoor kitchen openings — flush-install friendly."
          collectionType="counter-depth"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
